import { plainToClass } from "class-transformer";
import { AppUserExtended, JsonItem, PickListItem, IControllerFilter, PickListItemSorted } from "../../list-management/scripts/list-management.objects";
import { FilterTab, Filter, DropdownGroup } from "../../list-management/scripts/list-management.filters";
import { DeliverablesTableConfig, DeliverableParentExtended, DeliverableParent, DeliverableOptGroupFilterItem, DeliverableChildExtended, DeliverablesEditControllerConfig, DeliverablesCommentConfig, DeliverablesAttachmentsConfig, ViewDeliverablesModalConfig, DeliverableChild } from "./deliverables-tracker.objects";
import { InitializeFilterDropdown } from "../../../shared/scripts/dropdown-functions";  
import { getDisplayDate } from '../../../shared/scripts/date-functions';
import { TableColumnGroup, TableColumnVisibilityControllerV2 } from "../../../shared/scripts/table-column-visibility";
import { EditDeliverablesController, AddDeliverablesController, DeleteDeliverableController } from "./deliverables-tracker.controllers";
import { EditDeliverablesCommentController } from "./deliverables-tracker.comments";
import { EditDeliverablesAttachmentsController } from "./deliverables-tracker.attachments";
import { ViewDeliverablesController } from "./deliverables-tracker.modal";
import _ from "lodash";

export class DeliverablesTable implements IControllerFilter {
    // Config Variables
    tableContainerId: string;
    getDeliverablesRoute: string;
    loadingContainerId: string;
    loadedContentId: string;
    isAdmin: boolean;
    isEditable: boolean;
    tableVarName: string;
    viewDeliverableModalId: string;
    additionalInfoModalId: string;

    // Table Variables
    id: string = "deliverables-tracker";
    deliverableParents: DeliverableParentExtended[] = [];
    pickLists: JsonItem[] = [];
    sortedPickLists: JsonItem[] = [];
    allPickLists: JsonItem[] = [];
    deliverableTable: any;
    childDeliverableTable: any;
    tableColumnGroups: TableColumnGroup[];
    tableController: TableColumnVisibilityControllerV2;
    currentTargetItem: any;
    visibleDeliverableParents: DeliverableParentExtended[] = [];
    showUserGuide: boolean = false;
    userGuideLink: string = "";

    // Edit Controllers
    editParentController: EditDeliverablesController;
    editChildController: EditDeliverablesController;
    editChildCommentController: EditDeliverablesCommentController;
    editChildAttachmentsController: EditDeliverablesAttachmentsController;

    // Add Controllers
    addParentController: AddDeliverablesController;
    addChildController: AddDeliverablesController;
    deleteDeliverableController: DeleteDeliverableController;

    // View Controller
    viewModalController: ViewDeliverablesController;

    // TBD (may move these to config if needed)
    filterTabs: FilterTab[];
    columns: any[];
    childColumns: any[];

    constructor(config: DeliverablesTableConfig) {
        this.tableContainerId = config.tableContainerId;
        this.getDeliverablesRoute = config.getDeliverablesRoute;
        this.loadingContainerId = config.loadingContainerId;
        this.loadedContentId = config.loadedContentId;
        this.isEditable = config.isEditable;
        this.isAdmin = config.isAdmin;
        this.tableVarName = config.tableVarName;
        this.viewDeliverableModalId = config.viewDeliverableModalId;
        this.showUserGuide = config.showUserGuide;
        this.userGuideLink = config.userGuideLink;
        this.additionalInfoModalId = config.additionalInfoModalId;

        this.filterTabs = [
            new FilterTab("deliverables-overview", "Overview", "Overview",
                [
                    new Filter("deliverable-center-filter", "Center", "multi-select-pl-single", "DeliverableParent.CenterId", "pl-center"),
                    new Filter("deliverable-core-filter", "Core", "multi-select-pl-multi", "CoreIds", "pl-core"),
                    new Filter("deliverable-project-filter", "Project", "multi-select-pl-multi", "ProjectIds", "pl-project"),
                    new Filter("deliverable-status-filter", "Status", "multi-select-pl-single", "StatusId", "pl-deliverable-status"),
                    new Filter("deliverable-category-filter", "Category", "deliverable-category", "DeliverableParent.CategoryId", "pl-deliverable-category"),
                    new Filter("deliverable-subcategory-filter", "Subcategory", "deliverable-subcategory", "SubcategoryIds", "pl-deliverable-category", ["DeliverableParent.CategroyId"], ["pl-subcategory-vaccine", "pl-subcategory-antibody", "pl-subcategory-knowledge", "pl-subcategory-coord"]),
                    new Filter("deliverable-furthest-stage-filter", "Furthest Stage", "deliverable-furthest-stage", "FurthestStageId", "pl-deliverable-category", ["DeliverableParent.CategoryId"], ["pl-stage-vaccine", "pl-stage-antibody", "pl-stage-knowledge", "pl-stage-coord"]),
                    new Filter("deliverable-project-year-filter", "Project Year", "multi-select-pl-multi", "ProjectYearIds", "pl-project-year"),
                    new Filter("deliverable-trl-filter", "Technology Readiness Level (TRL)", "trl-tag", "DeliverableParent.TrlTagId", "pl-trl-tag"),
                    new Filter("deliverable-responsible-person-filter", "Responsible Person", "deliverable-responsible-person", "DeliverableChildren", "pl-users")
                ]
            ),
            new FilterTab("deliverables-timeline", "Timeline", "Timeline",
                [
                    new Filter("deliverable-planned-start-filter", "Planned Start Date", "date-before-after", "DeliverableParent.PlannedStartDate"),
                    new Filter("deliverable-planned-end-filter", "Planned End Date", "date-before-after", "DeliverableParent.PlannedEndDate"),
                    new Filter("deliverable-actual-start-filter", "Actual Start Date", "date-before-after", "DeliverableParent.ActualStartDate"),
                    new Filter("deliverable-actual-end-filter", "Actual End Date", "date-before-after", "DeliverableParent.ActualEndDate"),
                ]
            ),
            new FilterTab("deliverables-virus-focus", "Virus Focus", "Virus Focus",
                [
                    new Filter("deliverable-virus-family-focus-filter", "Virus Family Focus", "multi-select-pl-multi", "VirusFamilyIds", "pl-virus-family"),
                    new Filter("deliverable-prototype-virus-filter", "Prototype Virus", "multi-select-pl-multi", "DeliverableParent.PrototypeVirusIds", "pl-virus"),
                    new Filter("deliverable-test-virus-filter", "Test Virus", "multi-select-pl-multi", "DeliverableParent.TestVirusIds", "pl-virus"),
                    new Filter("deliverable-virus-target-filter", "Virus Target", "multi-select-pl-multi", "DeliverableParent.TargetIds", "pl-virus-target")
                ]
            )
        ]

        let descriptionCol: any = {
            data: null,
            title: "Description",
            className: `text-left font-size12 table-column-lg lh-normal deliverable-table-link`,
            render: (data: DeliverableParentExtended) => { return data.DeliverableParent.Description }
        }
        if (this.isEditable) {
            descriptionCol = { data: "DeliverableParent.Description", title: "Description", className: `text-left font-size12 table-column-md lh-normal` }
        }

        this.columns = [
            descriptionCol,
            { data: "DeliverableParent.CenterId", title: "Affiliation", className: "overview text-left font-size12 lh-normal table-column-sm", render: (data) => { return this.renderPickListItem(data, "pl-center", false, false) } },
            { data: null, title: "Status", className: "overview text-left font-size12 table-column-sm", render: (data) => { return this.renderDeliverableItem(data, 4) } },
            { data: "DeliverableParent.CategoryId", title: "Category", className: "overview text-left font-size12 table-column-sm lh-normal", render: (data) => { return this.renderPickListItem(data, "pl-deliverable-category", false, true)} },
            { data: null, title: "Subcategory", className: "overview text-left font-size12 table-column-sm lh-normal", render: (data, type) => { return "" } },
            { data: null, title: "Furthest Stage", className: "overview text-left font-size12 table-column-sm lh-normal", render: (data) => { return this.renderDeliverableItem(data, 1) } },
            { data: "DeliverableParent.TrlTagId", title: "TRL", className: "overview text-left font-size12 table-column-xs lh-normal", render: (data) => { return this.renderPickListItem(data, "pl-trl-tag", false, true) } },

            {
                data: function (row: DeliverableParentExtended, type, val, meta) {
                    if (type === "sort") {
                        return row.DeliverableParent.PlannedStartDate;
                    }
                    else if (type === "display" || type === "filter") {
                        return getDisplayDate(row.DeliverableParent.PlannedStartDate);
                    }
                    return row.DeliverableParent.PlannedStartDate
                },
                title: "Planned Start Date",
                className: "timeline text-left font-size12 nowrap"
            },
            {
                data: function (row: DeliverableParentExtended, type, val, meta) {
                    if (type === "sort") {
                        return row.DeliverableParent.PlannedEndDate;
                    }
                    else if (type === "display" || type === "filter") {
                        return getDisplayDate(row.DeliverableParent.PlannedEndDate);
                    }
                    return row.DeliverableParent.PlannedEndDate
                },
                title: "Planned End Date",
                className: "timeline text-left font-size12 nowrap"
            },
            {
                data: function (row: DeliverableParentExtended, type, val, meta) {
                    if (type === "sort") {
                        return row.DeliverableParent.ActualStartDate;
                    }
                    else if (type === "display" || type === "filter") {
                        return getDisplayDate(row.DeliverableParent.ActualStartDate);
                    }
                    return row.DeliverableParent.ActualStartDate
                },
                title: "Actual Start Date",
                className: "timeline text-left font-size12 nowrap"
            },
            {
                data: function (row: DeliverableParentExtended, type, val, meta) {
                    if (type === "sort") {
                        return row.DeliverableParent.ActualEndDate;
                    }
                    else if (type === "display" || type === "filter") {
                        return getDisplayDate(row.DeliverableParent.ActualEndDate);
                    }
                    return row.DeliverableParent.ActualEndDate
                },
                title: "Actual End Date",
                className: "timeline text-left font-size12 nowrap"
            },

            { data: "VirusFamilyIds", title: "Virus Families", className: "virus-focus text-left font-size12 table-column-md lh-normal", render: (data) => { return this.renderPickListItem(data, "pl-virus-family", true, false) } },
            { data: "DeliverableParent.PrototypeVirusIds", title: "Prototype Viruses", className: "virus-focus text-left font-size12 table-column-md lh-normal", render: (data) => { return this.renderPickListItem(data, "pl-virus", true, false) } },
            { data: "DeliverableParent.TestVirusIds", title: "Test Viruses", className: "virus-focus text-left font-size12 table-column-md lh-normal", render: (data) => { return this.renderPickListItem(data, "pl-virus", true, false) } },
            { data: "DeliverableParent.TargetIds", title: "Virus Targets", className: "virus-focus text-left font-size12 table-column-md lh-normal", render: (data) => { return this.renderPickListItem(data, "pl-virus-target", true, false, "<br/>") } },

            { data: "ProjectYearIds", title: "Year", className: "overview timeline text-left font-size12 table-column-xs lh-normal", render: (data) => { return this.renderPickListItem(data, "pl-project-year", true, false) } },
            {
                data: function (row: DeliverableParentExtended, type, val, meta) {
                    if (type === "sort") {
                        return row.LastUpdated;
                    }
                    else if (type === "display" || type === "filter") {
                        return getDisplayDate(row.LastUpdated);
                    }
                    return row.LastUpdated
                },
                title: "Last Updated",
                className: "text-left font-size12 nowrap"
            }
        ]

        let descriptionChildCol: any = {
            data: null,
            title: "Description",
            className: `text-left font-size12 table-column-lg lh-normal deliverable-table-child-link`,
            render: (data: DeliverableChildExtended) => { return data.DeliverableChild.Description }
        }
        if (this.isEditable) {
            descriptionChildCol = { data: "DeliverableChild.Description", title: "Description", className: `text-left font-size12 table-column-md lh-normal` }
        }

        this.childColumns = [
            descriptionChildCol,
            { data: null, title: "Affiliation", className: "overview text-left font-size12 lh-normal table-column-sm", render: (data) => { return this.renderDeliverableChildItem(data, 3) } },
            { data: "StatusId", title: "Status", className: "overview text-left font-size12 table-column-sm", render: (data) => { return this.renderPickListItem(data, "pl-deliverable-status", false, false) } },
            { data: "DeliverableChild.CategoryId", title: "Category", className: "overview text-left font-size12 table-column-sm lh-normal", render: (data) => { return this.renderPickListItem(data, "pl-deliverable-category", false, true) } },
            { data: null, title: "Subcategory", className: "overview text-left font-size12 table-column-sm lh-normal", render: (data, type) => { return this.renderDeliverableChildItem(data, 2) } },
            { data: null, title: "Stage", className: "overview text-left font-size12 table-column-sm lh-normal", render: (data) => { return this.renderDeliverableChildItem(data, 1) } },
            { data: null, title: "TRL", className: "overview text-left font-size12 table-column-xs lh-normal", render: (data) => { return "" } },
            { data: null, title: "Planned Start Date", className: "timeline text-left font-size12 nowrap", visible: false, render: (data) => { return "" } },
            { data: null, title: "Planned End Date", className: "timeline text-left font-size12 nowrap", visible: false, render: (data) => { return "" } },
            { data: null, title: "Actual Start Date", className: "timeline text-left font-size12 nowrap", visible: false, render: (data) => { return "" } },
            { data: null, title: "Actual End Date", className: "timeline text-left font-size12 nowrap", visible: false, render: (data) => { return "" } },
            { data: null, title: "Virus Families", className: "virus-focus text-left font-size12 table-column-md lh-normal", visible: false, render: (data) => { return "" } },
            { data: null, title: "Prototype Viruses", className: "virus-focus text-left font-size12 table-column-md lh-normal", visible: false, render: (data) => { return "" } },
            { data: null, title: "Test Viruses", className: "virus-focus text-left font-size12 table-column-md lh-normal", visible: false, render: (data) => { return "" } },
            { data: null, title: "Virus Targets", className: "virus-focus text-left font-size12 table-column-md lh-normal", visible: false, render: (data) => { return "" } },
            { data: null, title: "Year", className: "overview timeline text-left font-size12 table-column-xs lh-normal", render: (data) => { return "" } },
            {
                data: function (row: DeliverableChildExtended, type, val, meta) {
                    if (type === "sort") {
                        return row.LastUpdated;
                    }
                    else if (type === "display" || type === "filter") {
                        return getDisplayDate(row.LastUpdated);
                    }
                    return row.LastUpdated
                },
                title: "Last Updated",
                className: "text-left font-size12 nowrap"
            }
        ];

        this.childDeliverableTable = undefined;

        this.tableColumnGroups = [
            new TableColumnGroup("Overview", true, [], "overview"),
            new TableColumnGroup("Timeline", false, [], "timeline"),
            new TableColumnGroup("Virus Focus", false, [], "virus-focus"),
        ]

        if (this.isEditable) {
            let parentEditConfig: DeliverablesEditControllerConfig = {
                isAdmin: config.isAdmin,
                isChild: false,
                modalIdTarget: config.editModalId,
                submitRoute: config.postParentRoute,
                tableVarName: config.tableVarName
            }

            this.editParentController = new EditDeliverablesController(parentEditConfig, this);

            let childEditConfig: DeliverablesEditControllerConfig = {
                isAdmin: config.isAdmin,
                isChild: true,
                modalIdTarget: config.editModalId,
                submitRoute: config.postChildRoute,
                tableVarName: config.tableVarName
            }

            this.editChildController = new EditDeliverablesController(childEditConfig, this);

            let childCommentConfig: DeliverablesCommentConfig = {
                getCommentsChildRoute: config.getCommentsChildRoute,
                postCommentChildRoute: config.postCommentChildRoute,
                modalIdTarget: config.commentModalId
            }
            this.editChildCommentController = new EditDeliverablesCommentController(childCommentConfig);

            let attachmentsEditConfig: DeliverablesAttachmentsConfig = {
                getAttachmentsRoute: config.getAttachmentsRoute,
                addAttachmentsRoute: config.addAttachmentsRoute,
                removeAttachmentsRoute: config.removeAttachmentsRoute,
                modalIdTarget: config.attachmentModalId
            }
            this.editChildAttachmentsController = new EditDeliverablesAttachmentsController(attachmentsEditConfig);

            let parentAddConfig: DeliverablesEditControllerConfig = {
                isAdmin: config.isAdmin,
                isChild: false,
                modalIdTarget: config.addModalId,
                submitRoute: config.postParentRoute,
                tableVarName: config.tableVarName
            }
            this.addParentController = new AddDeliverablesController(parentAddConfig, this);

            let childAddConfig: DeliverablesEditControllerConfig = {
                isAdmin: config.isAdmin,
                isChild: true,
                modalIdTarget: config.addModalId,
                submitRoute: config.postChildRoute,
                tableVarName: config.tableVarName
            }
            this.addChildController = new AddDeliverablesController(childAddConfig, this);

            if (this.isAdmin) {
                this.deleteDeliverableController = new DeleteDeliverableController(this);
            }
        }
        else {
            let viewModalConfig: ViewDeliverablesModalConfig = {
                modalIdTarget: config.viewDeliverableModalId,
                getAttachmentsRoute: config.getAttachmentsRoute,
                getCommentsRoute: config.getCommentsChildRoute
            }
            this.viewModalController = new ViewDeliverablesController(viewModalConfig);
        }

        this.initialize();
    }

    async initialize() {
        this.loadingDisplay(true);

        let result: any[] = await this.getDeliverables();
        if (result["items"]) {
            this.deliverableParents = plainToClass(DeliverableParentExtended, <DeliverableParentExtended[]>result["items"]);
        }
        if (result["pickLists"]) {
            this.pickLists = plainToClass(JsonItem, <JsonItem[]>result["pickLists"]);
        }
        if (result["sortedPickLists"]) {
            this.sortedPickLists = plainToClass(JsonItem, <JsonItem[]>result["sortedPickLists"]);
        }

        this.allPickLists = [...this.pickLists, ...this.sortedPickLists];

        this.createHtmlTemplate();
        this.initializeFilterAndTable();

        this.loadingDisplay(false);
        this.resizeTable();
    }

    async getDeliverables() {
        let result: any;
        try {
            result = await $.ajax(this.getDeliverablesRoute, {
                method: "GET",
                headers: { "RequestVerificationToken": <string>$('input[name="__RequestVerificationToken"]').val() },
                cache: false
            });
            return result;
        }
        catch (error) {
            alert("An error has occurred, please refresh the page and try again.");
            console.log(error);
        }
    }

    loadingDisplay(isLoading: boolean): void {
        if (isLoading) {
            $(`#${this.loadedContentId}`).addClass("d-none");
            $(`#${this.loadingContainerId}`).removeClass("d-none");
        }
        else {
            $(`#${this.loadedContentId}`).removeClass("d-none");
            $(`#${this.loadingContainerId}`).addClass("d-none");
        }
    }

    createHtmlTemplate(): void {
        let title = "";
        let displayTitle = "";
        if (this.isAdmin) {
            displayTitle = `
                <div class="content-title mr-4">
                    Deliverables
                </div>
            `;
        }

        let additionalInfoBlock = "";
        if (this.additionalInfoModalId) {
            additionalInfoBlock = `
                <div class="ml-2">
                    <button id="${this.id}-additional-info-btn" type="button" class="btn btn-circle btn-sm">
                        <i class="fas fa-question"></i>
                    </button>
                </div>
            `
        }

        if (this.isEditable) {
            // If there is a user guide
            if (this.showUserGuide && this.userGuideLink) {
                title = `
                    <div class='d-flex justify-content-between mb-4'>
                        <div class="d-flex align-items-center">
                            ${displayTitle}
                            <div id="add-new-container" class="">
                                <button class="btn btn-small revampp-blue-btn" onclick="page.${this.tableVarName}.openAddModal('false')">+ Add New Parent Deliverable</button>
                            </div>
                        </div>
                        <div class="d-flex align-items-center">
                            <div>
                                <a class="btn-sm greenbtn text-decoration-none" target="_blank" href="${this.userGuideLink}">User Guide</a>
                            </div>
                            ${additionalInfoBlock}
                        </div>                
                    </div>
                `
            }
            else {
                title = `
                    <div class='d-flex justify-content-between mb-4'>
                        <div class="d-flex align-items-center">
                            ${displayTitle}
                            <div id="add-new-container" class="">
                                <button class="btn btn-small revampp-blue-btn" onclick="page.${this.tableVarName}.openAddModal('false')">+ Add New Parent Deliverable</button>
                            </div>
                        </div>
                        ${additionalInfoBlock}
                    </div>
                `
            }

        }

        // Create Content Blocks
        let content = `
            ${title}
            <div id="deliverable-filter-tab-container" class="d-flex justify-content-between">
                <ul class="nav nav-tabs justify-content-start border-bottom-0" id="deliverable-filter-tabs" role="tablist">
                    
                </ul>
                <div class="d-flex align-items-center">
                    <div class="font-italic pr-3" id="deliverable-table-count-found">50 Deliverables Found</div>
                    <div>
                        <button class="btn btn-sm btn-outline-secondary font-size12" onclick="page.${this.tableVarName}.resetFilters()">Reset Filters</button>
                    </div>
                </div>
            </div>

            <div class="tab-content edit-table-filter-tab-container" id="deliverable-filter-container">
                
            </div>

            <div class="list-table-container mt-4">
                <div class="revampp-datatable border-bottom" id="deliverable-table-container">
                    
                </div>
            </div>
            <div id="deliverable-detail-menu-container">

            </div>
        `

        $(`#${this.tableContainerId}`).empty().html(content);

        this.generateFilterTabContent();
       
    }

    initializeFilterAndTable() {
        // Filter Tabs
        this.generateFilterTabContent();

        // Select Table
        $("#deliverable-table-container").empty().html(
            `<table id="deliverables-select-table" class="table table-striped selectable-rows w-100" style="width:100%"></table>`
        )

        // Clear Detail Menu Container
        $("#deliverable-detail-menu-container").empty();

        this.initializeAdditionalInfoModal();

        // Create Table
        this.initializeTable();
    }

    generateFilterTabContent(): void {
        // Create new Tab Content
        let tabContent = "";
        let filtersContent = "";
        for (let i = 0; i < this.filterTabs.length; i++) {
            let currentFilter = this.filterTabs[i];
            let filterNumber = currentFilter.filters.length;
            if (i == 0) {
                tabContent += `<li class="nav-item"><a class="nav-link active" id="${currentFilter.id}-tab" data-toggle="tab" href="#${currentFilter.id}" role="tab" aria-controls="${currentFilter.id}" aria-selected="true">${currentFilter.displayName}</a></li>`
                filtersContent += `<div class="tab-pane fade show active" id="${currentFilter.id}" role="tabpanel" aria-labelledby="${currentFilter.id}-tab"><div class='row'>`
            }
            else {
                tabContent += `<li class="nav-item"><a class="nav-link" id="${currentFilter.id}-tab" data-toggle="tab" href="#${currentFilter.id}" role="tab" aria-controls="${currentFilter.id}" aria-selected="false">${currentFilter.displayName}</a></li>`
                filtersContent += `<div class="tab-pane fade" id="${currentFilter.id}" role="tabpanel" aria-labelledby="${currentFilter.id}-tab"><div class='row'>`
            }

            for (let j = 0; j < currentFilter.filters.length; j++) {
                let filter = currentFilter.filters[j];
                filtersContent += this.generateFilterHtml(filter, filterNumber);
            }

            filtersContent += `</div></div>`
        }

        // Update Tabs
        $("#deliverable-filter-tabs").empty().html(tabContent);
        $("#deliverable-filter-container").empty().html(filtersContent);

        // Generate Dropdown
        for (let i = 0; i < this.filterTabs.length; i++) {
            let currentFilterTab = this.filterTabs[i];
            for (let j = 0; j < currentFilterTab.filters.length; j++) {
                let currentFilter = currentFilterTab.filters[j];
                this.initializeFilterDropdown(currentFilter);
            }
        }

        this.filterTabs.forEach(tab => {
            if (tab.columnViewName) {
                $(`#${tab.id}-tab`).on('click', () => {
                    $(`#column-filter-dropdown-${this.id}-table-column-visibility`).val([`${tab.columnViewName}`])
                    $(`#column-filter-dropdown-${this.id}-table-column-visibility`).trigger("change");
                })
            }
        });
    }

    generateFilterHtml(filter: Filter, numberFilters: number): string {
        let content = ""
        if (filter.type == "deliverable-subcategory" || filter.type == "deliverable-furthest-stage" || filter.type == "trl-tag" || filter.type == "deliverable-category" || filter.type == "deliverable-responsible-person") {
            let liveSearch = "";
            if (filter.type == "deliverable-responsible-person") {
                liveSearch = `data-live-search="true"`;
            }

            content = `
                <div id="${filter.id}-select" class="col-12 col-md-3 mb-2">
                    <div id="${filter.id}-block" class="filter-label d-inline-block label-only">${filter.displayName}</div>
                    <select id="${filter.id}-dropdown" class="selectpicker dropdown" title="None Selected" data-actions-box="true" data-width="100%" data-container="body" data-size="10" ${liveSearch} multiple>
                    </select>
                </div>
            `
        }
        else {
            content = filter.generateFilterHtml();
        }

        // If there's more than 8 filters in a tab, make them smaller (5 per row)
        if (numberFilters > 8) {
            content = content.replace("col-md-3", "col-md-3 col-lg-2");
        }

        return content;
    }

    initializeAdditionalInfoModal(): void {
        if (this.additionalInfoModalId) {
            $(`#${this.id}-additional-info-btn`).off().on('click', () => {
                $(`#${this.additionalInfoModalId}`).modal('show');
            })
        }       
    }

    initializeFilterDropdown(filter: Filter): void {
        //filter.initializeDropdown(this.deliverableParents, this, foundPickList);
        if (filter.type == "deliverable-subcategory") {
            // Vaccine
            let vaccinePickList: PickListItemSorted[] = this.sortedPickLists.find(list => list.Id == "pl-subcategory-vaccine").Json;
            let vaccineGroup: DropdownGroup = new DropdownGroup("Vaccine", "1", null, vaccinePickList);

            // Antibody
            let antibodyPickList: PickListItemSorted[] = this.sortedPickLists.find(list => list.Id == "pl-subcategory-antibody").Json;
            let antibodyGroup: DropdownGroup = new DropdownGroup("Antibody", "2", null, antibodyPickList);

            // Knowledge
            let knowledgePickList: PickListItemSorted[] = this.sortedPickLists.find(list => list.Id == "pl-subcategory-knowledge").Json;
            let knowledgeGroup: DropdownGroup = new DropdownGroup("Generalizable Knowledge", "3", null, knowledgePickList);

            // Coordination
            let coordPickList: PickListItemSorted[] = this.sortedPickLists.find(list => list.Id == "pl-subcategory-coord").Json;
            let coordGroup: DropdownGroup = new DropdownGroup("Coordination & Sharing", "4", null, coordPickList);

            // No Subcategory
            let nonePickList: PickListItemSorted[] = [{ Id: 0, SortOrder: 0, Value: "None Specified" }]
            let noneGroup: DropdownGroup = new DropdownGroup("No Subcategories", "0", null, nonePickList);

            let dropdownGroups: DropdownGroup[] = [vaccineGroup, antibodyGroup, knowledgeGroup, coordGroup, noneGroup];
            this.initializeGroupFilterDropdown(filter, dropdownGroups);
        }
        else if (filter.type == "deliverable-furthest-stage") {
            // Vaccine
            let vaccinePickList: PickListItemSorted[] = this.sortedPickLists.find(list => list.Id == "pl-stage-vaccine").Json;
            let vaccineGroup: DropdownGroup = new DropdownGroup("Vaccine", "1", null, vaccinePickList);

            // Antibody
            let antibodyPickList: PickListItemSorted[] = this.sortedPickLists.find(list => list.Id == "pl-stage-antibody").Json;
            let antibodyGroup: DropdownGroup = new DropdownGroup("Antibody", "2", null, antibodyPickList);

            // Knowledge
            let knowledgePickList: PickListItemSorted[] = this.sortedPickLists.find(list => list.Id == "pl-stage-knowledge").Json;
            let knowledgeGroup: DropdownGroup = new DropdownGroup("Generalizable Knowledge", "3", null, knowledgePickList);

            // Coordination
            let coordPickList: PickListItemSorted[] = this.sortedPickLists.find(list => list.Id == "pl-stage-coord").Json;
            let coordGroup: DropdownGroup = new DropdownGroup("Coordination & Sharing", "4", null, coordPickList);

            // No Stage
            let nonePickList: PickListItemSorted[] = [{ Id: 0, SortOrder: 0, Value: "None Specified" }]
            let noneGroup: DropdownGroup = new DropdownGroup("No Stage", "0", null, nonePickList);

            let dropdownGroups: DropdownGroup[] = [vaccineGroup, antibodyGroup, knowledgeGroup, coordGroup, noneGroup];
            this.initializeGroupFilterDropdown(filter, dropdownGroups);
        }
        else if (filter.type == "trl-tag") {
            let trlPickList: PickListItemSorted[] = this.sortedPickLists.find(list => list.Id == "pl-trl-tag").Json;
            let options: any[] = [];
            trlPickList.sort((x1, x2) => x1.SortOrder - x2.SortOrder);
            trlPickList.forEach(item => options.push([item.Id.toString(), item.Value]));
            options.push(["0", "N/A"]);
            InitializeFilterDropdown(`#${filter.id}-dropdown`, null, this, options, true, false);
        }
        else if (filter.type == "deliverable-category") {
            let categoryPickList: PickListItemSorted[] = this.sortedPickLists.find(list => list.Id == "pl-deliverable-category").Json;
            let options: any[] = [];
            categoryPickList.sort((x1, x2) => x1.SortOrder - x2.SortOrder);
            categoryPickList.forEach(item => options.push([item.Id.toString(), item.Value]))
            InitializeFilterDropdown(`#${filter.id}-dropdown`, null, this, options, true, false);
        }
        else if (filter.type == "deliverable-responsible-person") {
            let userPickList: PickListItem[] = this.pickLists.find(list => list.Id == "pl-users").Json;
            let foundUserIds: any[] = [];
            // Get current responsible users
            this.deliverableParents.forEach(parent => {
                parent.DeliverableChildren.forEach(child => {
                    let responsibleUserId = child.DeliverableChild.ResponsiblePersonId;
                    if (responsibleUserId && !foundUserIds.includes(responsibleUserId)) {
                        foundUserIds.push(responsibleUserId);
                    }
                })
            });
            let options: string[][] = [];
            foundUserIds.forEach(userId => {
                let foundOption: PickListItem = userPickList.find(item => item.Id == userId);
                if (foundOption) {
                    options.push([foundOption.Id.toString(), foundOption.Value]);
                }
            });
            options.sort((x1, x2) => x1[1].localeCompare(x2[1]));
            options.push(["0", "N/A"]);
            InitializeFilterDropdown(`#${filter.id}-dropdown`, null, this, options, true, false);
        }
        // traditional filter
        else {
            if (filter.pickListName) {
                let foundPickList: PickListItem[] = this.pickLists.find(list => list.Id == filter.pickListName).Json;
                if (foundPickList) {
                    filter.initializeDropdown(this.deliverableParents, this, foundPickList);
                }
            }
            else {
                filter.initializeDropdown(this.deliverableParents, this);
            }
        }
    }

    initializeGroupFilterDropdown(filter: Filter, dropdownGroups: DropdownGroup[]): void {
        $(`#${filter.id}-dropdown`).selectpicker({
            dropupAuto: false,
            selectedTextFormat: "count",

            countSelectedText: (numSelected, numTotal) => {
                if (numSelected == numTotal) {
                    return "All";
                }
                else {
                    return numSelected + " Selected";
                }
            }
        });

        let optionContent = "";
        dropdownGroups.forEach(group => {
            optionContent += group.createOptGroupHtml();
        });

        $(`#${filter.id}-dropdown`).append(optionContent);

        $(`#${filter.id}-dropdown`).selectpicker("refresh");

        $(`#${filter.id}-dropdown`).on('changed.bs.select', () => {
            this.triggerFilter();
        });
    }

    getSelectedFilterTabId(): string {
        let selectedTab = $("#deliverable-filter-tabs").find(".active").first();
        return selectedTab.prop("id");
    }

    triggerFilter(): void {
        let filteredItems: any[] = _.cloneDeep(this.deliverableParents);

        this.filterTabs.forEach(tab => {
            tab.filters.forEach(filter => {
                if (filter.type == "deliverable-subcategory") {
                    filteredItems = this.filterSubcategory(filteredItems, filter);
                }
                else if (filter.type == "deliverable-furthest-stage") {
                    filteredItems = this.filterFurthestStage(filteredItems, filter);
                }
                else if (filter.type == "trl-tag") {
                    let selectedOptions = <string[]>$(`#${filter.id}-dropdown`).val();
                    filteredItems = filteredItems.filter((item: DeliverableParentExtended) => selectedOptions.includes(item.DeliverableParent.TrlTagId.toString()));
                }
                else if (filter.type == "deliverable-category") {
                    let selectedOptions = <string[]>$(`#${filter.id}-dropdown`).val();
                    filteredItems = filteredItems.filter((item: DeliverableParentExtended) => selectedOptions.includes(item.DeliverableParent.CategoryId.toString()));
                }
                else if (filter.type == "deliverable-responsible-person") {
                    let selectedOptions = <string[]>$(`#${filter.id}-dropdown`).val();
                    filteredItems = filteredItems.filter((item: DeliverableParentExtended) => {
                        if (selectedOptions.includes("0") && item.DeliverableChildren.length == 0) {
                            return true;
                        }
                        return item.DeliverableChildren.some((child: DeliverableChildExtended) => {
                            return selectedOptions.includes(child?.DeliverableChild?.ResponsiblePersonId?.toString())
                        })
                    });
                }
                else {
                    filteredItems = filter.filterItems(filteredItems);
                }
            })
        });

        this.updateTable(filteredItems);
    }

    filterSubcategory(items: DeliverableParentExtended[], filter: Filter): DeliverableParentExtended[] {
        // Get selected items from filter 
        let selectedItems: DeliverableOptGroupFilterItem[] = [];
        let selectedOptions = $(`#${filter.id}-dropdown`).find("option:selected");
        let isNoneSelected: boolean = false;
        selectedOptions.each(function () {
            let valueStr = $(this).val();
            let valueId = parseInt(valueStr.toString());
            if (valueId == 0) isNoneSelected = true;

            let parentOptGroup = $(this).closest("optgroup");
            let categoryIdStr = parentOptGroup.data("groupId");
            if (categoryIdStr) {
                let categoryId = parseInt(categoryIdStr);
                if (categoryId && valueId) {
                    selectedItems.push(new DeliverableOptGroupFilterItem(categoryId, valueId))
                }
            }
        });

        // Filter
        items = items.filter(item => {
            let subcategories = item.SubcategoryIds.split("|");
            if (item.SubcategoryIds == "" && isNoneSelected) {
                return true;
            }
            if (selectedItems.some(selectedItem => selectedItem.categoryId == item.DeliverableParent.CategoryId && subcategories.includes(selectedItem.itemId.toString()))) {
                return true;
            }
            else {
                return false;
            }
        });

        return items;
    }

    filterFurthestStage(items: DeliverableParentExtended[], filter: Filter): DeliverableParentExtended[] {
        // Get selected items from filter
        let selectedItems: DeliverableOptGroupFilterItem[] = [];
        let selectedOptions = $(`#${filter.id}-dropdown`).find("option:selected");
        selectedOptions.each(function () {
            let valueStr = $(this).val();
            let valueId = parseInt(valueStr.toString());

            let parentOptGroup = $(this).closest("optgroup");
            let categoryIdStr = parentOptGroup.data("groupId");
            if (categoryIdStr) {
                let categoryId = parseInt(categoryIdStr);
                if (categoryId && valueId) {
                    selectedItems.push(new DeliverableOptGroupFilterItem(categoryId, valueId))
                }
            }
        });

        // Filter
        items = items.filter(item => {
            let furthestStageId = item.FurthestStageId;
            if (selectedItems.some(selectedItem => selectedItem.categoryId == item.DeliverableParent.CategoryId && selectedItem.itemId == furthestStageId)) {
                return true;
            }
            else {
                return false;
            }
        });

        return items;
    }


    resetFilters(): void {
        this.filterTabs.forEach(tab => {
            tab.filters.forEach(filter => {
                if (filter.type == "deliverable-subcategory") {
                    $(`#${filter.id}-dropdown`).selectpicker("selectAll");
                }
                else if (filter.type == "deliverable-furthest-stage") {
                    $(`#${filter.id}-dropdown`).selectpicker("selectAll");
                }
                else if (filter.type == "trl-tag") {
                    $(`#${filter.id}-dropdown`).selectpicker("selectAll");
                }
                else if (filter.type == "deliverable-category") {
                    $(`#${filter.id}-dropdown`).selectpicker("selectAll");
                }
                else if (filter.type == "deliverable-responsible-person") {
                    $(`#${filter.id}-dropdown`).selectpicker("selectAll");
                }
                else {
                    filter.resetFilter();
                }
            })
        });

        this.updateTable(this.deliverableParents);
    }

    renderPickListItem(data: any, pickListName: string, isMultiple: boolean = false, isSorted: boolean = false, separator: string = ", "): string {
        if (!data) {
            return "";
        }

        let sortedPickList: PickListItemSorted[] = [];
        let pickList: PickListItem[] = [];

        if (isSorted) {
            let foundList: PickListItemSorted[] = this.sortedPickLists.find(list => list.Id == pickListName)?.Json;
            if (foundList) {
                sortedPickList = foundList;
            }
        }
        else {
            let foundList: PickListItem[] = this.pickLists.find(list => list.Id == pickListName)?.Json;
            if (foundList) {
                pickList = foundList;
            }
        }

        if (sortedPickList.length == 0 && pickList.length == 0) {
            return "";
        }

        let returnStrArray: string[] = [];
        let returnStr: string = "";
        if (isMultiple) {
            let stringArrIds: string[] = data.split("|");
            stringArrIds = _.uniq(stringArrIds);

            for (let i = 0; i < stringArrIds.length; i++) {
                let numId: number = parseInt(stringArrIds[i]);
                if (numId) {
                    let foundItem = null;
                    if (isSorted) {
                        foundItem = sortedPickList.find(item => item.Id == numId);
                    }
                    else {
                        foundItem = pickList.find(item => item.Id == numId);
                    }

                    if (foundItem) {
                        returnStrArray.push(foundItem.Value);
                    }
                }
            }

            returnStrArray.sort();
            returnStr = returnStrArray.join(separator);
        }
        else {
            let foundItem = null;
            if (isSorted) {
                foundItem = sortedPickList.find(item => item.Id == data);
            }
            else {
                foundItem = pickList.find(item => item.Id == data);
            }

            if (foundItem) {
                returnStr = foundItem.Value;
            }
        }

        return returnStr;
    }

    renderDeliverableItem(data: DeliverableParentExtended, typeId: number): string {
        // Furthest Stage
        if (typeId == 1) {
            if (!data.FurthestStageId) {
                return "";
            }

            let stageList: PickListItemSorted[] = [];
            // Vaccine
            if (data.DeliverableParent.CategoryId == 1) {
                let foundList = this.sortedPickLists.find(list => list.Id == "pl-stage-vaccine")?.Json;
                if (foundList) stageList = foundList;
            }
            else if (data.DeliverableParent.CategoryId == 2) {
                let foundList = this.sortedPickLists.find(list => list.Id == "pl-stage-antibody")?.Json;
                if (foundList) stageList = foundList;
            }
            else if (data.DeliverableParent.CategoryId == 3) {
                let foundList = this.sortedPickLists.find(list => list.Id == "pl-stage-knowledge")?.Json;
                if (foundList) stageList = foundList;
            }
            else if (data.DeliverableParent.CategoryId == 4) {
                let foundList = this.sortedPickLists.find(list => list.Id == "pl-stage-coord")?.Json;
                if (foundList) stageList = foundList;
            }

            let foundItem = stageList.find(item => item.Id == data.FurthestStageId);
            if (foundItem) {
                return foundItem.Value;
            }
            else {
                return "";
            }
        }

        // Expand Button 
        if (typeId == 2) {
            let content = "";
            if (data.DeliverableChildren.length > 0) {
                content = `<i class="fa-solid fa-circle-plus fa-lg expand-icon"></i>`
            }

            return content;
        }

        // Last Updated?
        if (typeId == 3) {
            return getDisplayDate(data.LastUpdated);
        }

        // Status Bar
        if (typeId == 4) {
            let displayIcon = `<i class="fa-regular fa-circle"></i>`;
            if (data.IsOngoing) displayIcon = `<i class="fa-solid fa-circle-half-stroke"></i>`;
            else if (data.IsComplete) displayIcon = `<i class="fa-solid fa-circle-check"></i>`;
            let totalCount = data.DeliverableChildren.length;
            let completeCount = data.DeliverableChildren.filter(child => child.IsComplete).length;
            let percentComplete: string = Math.round(100 * (completeCount / totalCount)).toString();

            let completeClass = "";
            if (data.IsComplete) completeClass = "completed-bar";

            let returnContent = `
                <div class="status-bar-container">
                    <div class="status-bar">
                        <div class="percent-complete-bar ${completeClass}" style="width:${percentComplete}%"></div>
                        <div class="percent-complete-text d-flex justify-content-between">
                            <div>
                                <span class="status-bar-icon">${displayIcon}</span>
                            </div>
                            <div>
                                ${completeCount} / ${totalCount}
                            </div>                          
                        </div>
                    </div>
                </div>
            `
            return returnContent;
        }
    }

    renderDeliverableChildItem(data: DeliverableChildExtended, typeId: number) {
        // Stage
        if (typeId == 1) {
            if (!data.DeliverableChild.StageId) {
                return "";
            }

            let stageList: PickListItemSorted[] = [];
            // Vaccine
            if (data.DeliverableChild.CategoryId == 1) {
                let foundList = this.sortedPickLists.find(list => list.Id == "pl-stage-vaccine")?.Json;
                if (foundList) stageList = foundList;
            }
            else if (data.DeliverableChild.CategoryId == 2) {
                let foundList = this.sortedPickLists.find(list => list.Id == "pl-stage-antibody")?.Json;
                if (foundList) stageList = foundList;
            }
            else if (data.DeliverableChild.CategoryId == 3) {
                let foundList = this.sortedPickLists.find(list => list.Id == "pl-stage-knowledge")?.Json;
                if (foundList) stageList = foundList;
            }
            else if (data.DeliverableChild.CategoryId == 4) {
                let foundList = this.sortedPickLists.find(list => list.Id == "pl-stage-coord")?.Json;
                if (foundList) stageList = foundList;
            }

            let foundItem = stageList.find(item => item.Id == data.DeliverableChild.StageId);
            if (foundItem) {
                return foundItem.Value;
            }
            else {
                return "";
            }
        }

        // Subcategories
        else if (typeId == 2) {
            if (!data.DeliverableChild.SubcategoryIds) {
                return "";
            }

            let subcategoryList: PickListItemSorted[] = [];
            // Vaccine
            if (data.DeliverableChild.CategoryId == 1) {
                let foundList = this.sortedPickLists.find(list => list.Id == "pl-subcategory-vaccine")?.Json;
                if (foundList) subcategoryList = foundList;
            }
            else if (data.DeliverableChild.CategoryId == 2) {
                let foundList = this.sortedPickLists.find(list => list.Id == "pl-subcategory-antibody")?.Json;
                if (foundList) subcategoryList = foundList;
            }
            else if (data.DeliverableChild.CategoryId == 3) {
                let foundList = this.sortedPickLists.find(list => list.Id == "pl-subcategory-knowledge")?.Json;
                if (foundList) subcategoryList = foundList;
            }
            else if (data.DeliverableChild.CategoryId == 4) {
                let foundList = this.sortedPickLists.find(list => list.Id == "pl-subcategory-coord")?.Json;
                if (foundList) subcategoryList = foundList;
            }

            let subCatArray: string[] = data.DeliverableChild.SubcategoryIds.split("|");
            let returnVals: string[] = [];
            subCatArray.forEach(subcat => {
                let foundItem = subcategoryList.find(item => item.Id.toString() == subcat);
                if (foundItem) {
                    returnVals.push(foundItem.Value);
                }
            })

            returnVals.sort();
            return returnVals.join(", ");
        }

        // Center/Core/Project
        else if (typeId == 3) {
            let returnVal = "";
            if (data.DeliverableChild.CoreId > 0) {
                let pickList: PickListItem[] = this.pickLists.find(list => list.Id == "pl-core")?.Json;
                if (pickList) {
                    let foundItem = pickList.find(item => item.Id == data.DeliverableChild.CoreId);
                    if (foundItem) {
                        let strArr = foundItem.Value.split(" - ");
                        strArr[0] = `<i>${strArr[0]}</i>`
                        returnVal = strArr.join("<br>");
                    }
                }
            }
            else if (data.DeliverableChild.ProjectId > 0) {
                let pickList: PickListItem[] = this.pickLists.find(list => list.Id == "pl-project")?.Json;
                if (pickList) {
                    let foundItem = pickList.find(item => item.Id == data.DeliverableChild.ProjectId);
                    if (foundItem) {
                        let strArr = foundItem.Value.split(" - ");
                        strArr[0] = `<i>${strArr[0]}</i>`
                        returnVal = strArr.join("<br>");
                    }
                }
            }
            else if (data.DeliverableChild.CenterId > 0) {
                let pickList: PickListItem[] = this.pickLists.find(list => list.Id == "pl-center")?.Json;
                if (pickList) {
                    let foundItem = pickList.find(item => item.Id == data.DeliverableChild.CenterId);
                    if (foundItem) {
                        returnVal = foundItem.Value;
                    }
                }
            }

            return returnVal;
        }
        
    }

    initializeTable(): void {
        let columns = _.cloneDeep(this.columns);

        let tableColumns: any[] = [
            { data: null, title: ``, orderable: false, className: "text-center item-expand-option table-column-icon", render: (data) => { return this.renderDeliverableItem(data, 2)} }
        ]
        let firstColumn = columns.shift();
        if (firstColumn) {
            tableColumns.push(firstColumn);
            if (this.isEditable) {
                tableColumns.push(
                    { data: "DeliverableParent.Id", title: "", width: "26px", orderable: false, className: "text-center detail-btn-container", render: (data) => this.renderDetailButton(data) }
                )
            }
        }
        tableColumns.push(...columns);

        let fixedColCount = 1;
        if (this.isEditable) fixedColCount = 1;
        let plannedStartIndex = 8;
        if (this.isEditable) {
            plannedStartIndex = 9;
        }

        this.deliverableTable = $(`#deliverables-select-table`).DataTable({
            "dom": `<"${this.id}-top-controls"<"${this.id}-column-select"><"${this.id}-search-bar"f><"${this.id}-count-found"B>>rtip`,
            autoWidth: true,
            info: false,
            paging: false,
            search: true,
            scrollX: true,
            //responsive: false,
            scrollY: '60vh',
            scrollCollapse: true,
            orderCellsTop: true,
            fixedColumns: {
                leftColumns: fixedColCount
            },
            language:
            {
                search: "Search",
            },
            columns: tableColumns,
            buttons: [
                {
                    extend: 'csv',
                    filename: "revampp_deliverables",
                    text: '<i class="fas fa-file-download"></i>',
                    titleAttr: 'CSV',
                    charset: 'utf-8',
                    customize: () => {
                        return this.createCsvData();
                    }
                }
            ],
            data: this.deliverableParents,
            order: [[plannedStartIndex, 'asc']],
            initComplete: (settings, json) => {

            },
        });

        $(`.${this.id}-top-controls`).addClass('row mx-0 align-items-center');

        $(`.${this.id}-column-select`).addClass('col-12 col-md-3 px-0 mb-2');
        $(`.${this.id}-column-select`).empty().html(`<div id="${this.id}-column-select"></div>`);
        $(`.${this.id}-search-bar`).addClass('col-12 col-md-3');
        $(`.${this.id}-count-found`).addClass('col-12 col-md-6 d-flex justify-content-end vertical-align-middle mt-3 px-0');
        $(`.${this.id}-count-found`).prepend('<div id="selected-items-block" class="align-self-center pr-3"></div>');

        this.tableController = new TableColumnVisibilityControllerV2(`#deliverables-select-table`, `#${this.id}-column-select`, this.tableColumnGroups, `${this.id}-table-column-visibility`);

        $(`#${this.tableController.uniqueId}`).on('changed.bs.select', () => {
            this.updateChildColumnVisibility();
        });

        $(`#deliverables-select-table tbody`).on('click', 'td', (event) => {
            let td = $(event.currentTarget);
            if (td.hasClass("item-expand-option")) {

                // Update the parent row
                let tr = $(td).closest("tr");
                let row = this.deliverableTable.row(tr);
                let parent: DeliverableParentExtended = row.data();
                if (parent.DeliverableChildren.length == 0) {
                    // Don't do anything if the parent doesn't have any children
                    return;
                }
                let childTable = this.createChildTable();

                // Remove any existing child rows
                $("#deliverables-select-table").find("#deliverable-child-table-container").remove();
                if (tr.hasClass("expand-row")) {
                    row.child.hide();
                    tr.removeClass("expand-row");

                    // Also force hide any exisitng dropdown menus
                    $("#deliverable-detail-menu-container").find(".dropdown-menu").removeClass("show");
                }
                else {
                    // remove any existing expand rows
                    $("#deliverables-select-table").find(".expand-row").removeClass("expand-row");
                    tr.addClass("expand-row");
                    row.child(childTable).show();
                    this.initializeChildTable(row.data());
                    this.resizeChildTable();
                }

                // also hide any dropdown menus
                $("#detail-menu-container").find(".revampp-dropdown-menu").removeClass("show");
                return;
            }
            else if (td.hasClass("deliverable-table-link")) {
                let tr = $(td).closest("tr");
                let row = this.deliverableTable.row(tr);
                let parent: DeliverableParentExtended = row.data();

                if (parent) {
                    this.viewModalController.initialize(this.allPickLists, parent);
                    $(`#${this.viewDeliverableModalId}`).modal('show');
                }
            }
        });

        this.updateCount(this.deliverableParents.length);
        this.visibleDeliverableParents = this.deliverableParents;
    }

    updateChildColumnVisibility(): void {
        if (!this.childDeliverableTable) {
            return;
        }

        let values: string[] = <string[]>($(`#${this.tableController.uniqueId}`).val());
        let visibleStyles: string[] = [];
        let hiddenStyles: string[] = [];
        this.tableController.columnGroups.forEach((colgroup: TableColumnGroup) => {
            if (values.includes(colgroup.name)) {
                if (colgroup.columnStyle.length > 0) { visibleStyles.push(colgroup.columnStyle); }
            }
            else {
                if (colgroup.columnStyle.length > 0) { hiddenStyles.push(colgroup.columnStyle); }
            }
        });

        var table = this.childDeliverableTable;

        hiddenStyles.forEach((style: string) => {
            table.columns('.' + style).visible(false, false);
        });
        visibleStyles.forEach((style: string) => {
            table.columns('.' + style).visible(true, false);
        });

        table.columns.adjust().draw(false);
    }

    initializeChildTable(parent: DeliverableParentExtended): void {
        if (this.childDeliverableTable != undefined) {
            this.childDeliverableTable.destroy();
        }

        let columns = _.cloneDeep(this.childColumns);

        let tableColumns: any[] = [
            { data: null, title: ``, orderable: false, className: "text-center table-column-icon child-row-indicator", render: (data) => { return "" } }
        ]
        let firstColumn = columns.shift();
        if (firstColumn) {
            tableColumns.push(firstColumn);
            if (this.isEditable) {
                tableColumns.push(
                    { data: "DeliverableChild.Id", title: "", width: "26px", orderable: false, className: "text-center detail-btn-container", render: (data) => this.renderDetailButton(data) }
                )
            }
        }
        tableColumns.push(...columns);

        let lastUpdatedColIndex = tableColumns.length - 1;

        let fixedColCount = 1;
        if (this.isEditable) fixedColCount = 1;

        // Filter Child Items based on filters
        let filteredChildren: DeliverableChildExtended[] = this.filterChildTable(parent);

        this.childDeliverableTable = $(`#deliverable-child-select-table`).DataTable({
            "dom": `rtip`,
            autoWidth: true,
            info: false,
            paging: false,
            search: true,
            scrollX: true,
            //responsive: false,
            //scrollY: '60vh',
            scrollCollapse: true,
            orderCellsTop: true,
            fixedColumns: {
                leftColumns: fixedColCount
            },
            columns: tableColumns,
            data: filteredChildren,
            order: [[lastUpdatedColIndex, 'desc']],
            initComplete: (settings, json) => {

            },
        });

        this.updateChildColumnVisibility();

        $(`#deliverable-child-select-table tbody`).off().on('click', 'td', (event) => {
            let td = $(event.currentTarget);
            if (td.hasClass("deliverable-table-child-link")) {
                let tr = $(td).closest("tr");
                let row = this.childDeliverableTable.row(tr);
                let child: DeliverableChildExtended = row.data();
                let parent: DeliverableParentExtended = this.deliverableParents.find(parent => parent.DeliverableParent.Id == child.DeliverableChild.ParentId);
                if (child && parent) {
                    this.viewModalController.initialize(this.allPickLists, parent, child);
                    $(`#${this.viewDeliverableModalId}`).modal('show');
                }

            }
        });
    }

    filterChildTable(parent: DeliverableParentExtended): DeliverableChildExtended[] {
        let filteredChildren = parent.DeliverableChildren;

        // Filter for subcategory
        // Get selected items from filter 
        let selectedItems: DeliverableOptGroupFilterItem[] = [];
        let selectedOptions = $(`#deliverable-subcategory-filter-dropdown`).find("option:selected");
        let isNoneSelected: boolean = false;
        selectedOptions.each(function () {
            let valueStr = $(this).val();
            let valueId = parseInt(valueStr.toString());
            if (valueId == 0) isNoneSelected = true;

            let parentOptGroup = $(this).closest("optgroup");
            let categoryIdStr = parentOptGroup.data("groupId");
            if (categoryIdStr) {
                let categoryId = parseInt(categoryIdStr);
                if (categoryId && valueId) {
                    selectedItems.push(new DeliverableOptGroupFilterItem(categoryId, valueId))
                }
            }
        });

        filteredChildren = filteredChildren.filter((child: DeliverableChildExtended) => {
            let subcategories = child.DeliverableChild.SubcategoryIds.split("|");
            if (child.DeliverableChild.SubcategoryIds == "" && isNoneSelected) {
                return true;
            }
            if (selectedItems.some(selectedItem => selectedItem.categoryId == child.DeliverableChild.CategoryId && subcategories.includes(selectedItem.itemId.toString()))) {
                return true;
            }
            else {
                return false;
            }
        });

        // Filter for responsible Person
        let selectedResponsiblePersonOptions = <string[]>$(`#deliverable-responsible-person-filter-dropdown`).val();
        filteredChildren = filteredChildren.filter((child: DeliverableChildExtended) => selectedResponsiblePersonOptions.includes(child?.DeliverableChild?.ResponsiblePersonId?.toString()));

        return filteredChildren;
    }


    createChildTable(): any {
        let returnContent = `
            <div class="revampp-datatable datatable-no-header" id="deliverable-child-table-container">
                <table id="deliverable-child-select-table" class="table table-striped child-rows w-100" style="width:100%"></table>
            </div>
        `;

        return $(returnContent);
    }

    updateCount(numItems: number): void {
        let content = "";
        if (numItems == 1) {
            content = "1 Deliverable Found"
        }
        else {
            content = `${numItems.toString()} Deliverables Found`
        }

        $("#deliverable-table-count-found").text(content);
    }

    renderDetailButton(data: any): string {

        let content = `
            <div class="btn-group dropright">
                <button type="button" class="btn dropdown-toggle p-0" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" onclick="page.${this.tableVarName}.updateMenuPosition(event)">
                    <div class="item-detail-button" data-id="${data}"><div><i class="fa-solid fa-ellipsis"></i></div></div>
                </button>
                <div class="dropdown-menu revampp-dropdown-menu">
                    
                </div>
            </div>
        `
        return content;
    }

    updateMenuPosition(e: Event) {
        // set current selected row
        let targetElement = <JQuery<HTMLElement>>$(e.currentTarget);
        let table = $(targetElement).closest("table");
        let isChild = $(table).hasClass("child-rows");
        this.setTargetedRow(targetElement, isChild);

        // empty any existing menus (menus are moved to this div to avoid overflow issues with table)
        $("#deliverable-detail-menu-container").empty();

        let dropdownMenu = targetElement.siblings('.revampp-dropdown-menu');
        let dropdownContainer = targetElement.parent();

        // Check is the dropdown menu has been moved to the new #detail-menu-container 
        if (dropdownMenu.length <= 0) {
            // if the menu has already been moved, remove existing dropdwon relationship, and create a new one with new menu options
            targetElement.dropdown('dispose');
            dropdownContainer.append(`
                <div class="dropdown-menu revampp-dropdown-menu">
                    ${this.getMenuContent(isChild)}
                </div>
            `)
            targetElement.dropdown();
            // move the updated menu to #detail-menu-container when its shown
            dropdownContainer.off().on("show.bs.dropdown", (e) => {
                let updatedDropdownMenu = dropdownContainer.find('.revampp-dropdown-menu');
                $("#deliverable-detail-menu-container").append(<JQuery<HTMLElement>>$(updatedDropdownMenu))
            });
        }
        else {
            // if the menu has not already been moved (first click), just add the correct menu options.
            dropdownMenu.html(`
                ${this.getMenuContent(isChild)}
            `)
            // move the updated menu to #detail-menu-container when its shown
            dropdownContainer.off().on("show.bs.dropdown", (e) => {
                let updatedDropdownMenu = dropdownContainer.find(".revampp-dropdown-menu");
                $("#deliverable-detail-menu-container").append(<JQuery<HTMLElement>>$(updatedDropdownMenu));
            });
        }
    }

    setTargetedRow(target: JQuery<HTMLElement>, isChild: boolean): void {
        let tr = $(target).closest('tr');
        if (isChild) {
            let row = this.childDeliverableTable.row(tr);
            this.currentTargetItem = <DeliverableChildExtended>row.data();
        }
        else {
            let row = this.deliverableTable.row(tr);
            this.currentTargetItem = <DeliverableParentExtended>row.data();
        }
    }

    getMenuContent(isChild: boolean): string {

        let menuOptions = "";

        if (isChild) {
            this.editChildController.sections.forEach(section => {
                let option: string = `<a class="dropdown-item" onclick="page.${this.tableVarName}.openEditModal('${section.id}', '${isChild}')"><i class="${section.icon} dropdown-icon pr-2"></i> Edit ${section.title}</a>`
                menuOptions += option;
            })
            menuOptions += `<a class="dropdown-item" onclick="page.${this.tableVarName}.openCommentModal()"><i class="fa-solid fa-comments dropdown-icon pr-2"></i> Comments</a>`
            menuOptions += `<a class="dropdown-item" onclick="page.${this.tableVarName}.openAttachmentsModal()"><i class="fa-solid fa-file-arrow-up dropdown-icon pr-2"></i> Edit Attachments</a>`
            if (this.isAdmin) {
                menuOptions += "<hr>"
                menuOptions += `<a class="dropdown-item" onclick="page.${this.tableVarName}.openDeleteModal('true')"><i class="fa-solid fa-trash-can dropdown-icon red-icon pr-2"></i>Delete Deliverable</a>`
            }
        }
        else {
            this.editParentController.sections.forEach(section => {
                let option: string = `<a class="dropdown-item" onclick="page.${this.tableVarName}.openEditModal('${section.id}', '${isChild}')"><i class="${section.icon} dropdown-icon pr-2"></i> Edit ${section.title}</a>`
                menuOptions += option;
            })
            menuOptions += `<a class="dropdown-item" onclick="page.${this.tableVarName}.openAddModal('true')"><i class="fa-solid fa-plus dropdown-icon pr-2" style="color: #05af55"></i> Add Child</a>`
            if (this.isAdmin) {
                menuOptions += "<hr>"
                menuOptions += `<a class="dropdown-item" onclick="page.${this.tableVarName}.openDeleteModal('false')"><i class="fa-solid fa-trash-can dropdown-icon red-icon pr-2"></i>Delete Deliverable</a>`
            }
        }
        return menuOptions;       
    }

    resizeTable(): void {
        if (this.deliverableTable) {
            this.deliverableTable.columns.adjust().draw();
        }
    }

    resizeChildTable(): void {
        if (this.childDeliverableTable) {
            this.childDeliverableTable.columns.adjust().draw();
        }
    }

    updateTable(items: any[]) {
        // Removes any shown menus
        $("#detail-menu-container").empty();

        this.deliverableTable.clear();
        this.deliverableTable.rows.add(items).draw();
        this.updateCount(items.length);
        this.resizeTable();
        this.visibleDeliverableParents = items;
    }

    createCsvData(): string {
        let csv = "";
        let columnNames: string[] = [
            "Type", // 0
            "Description", // 1
            "Category", // 2
            "Status", // 3
            "Furthest Stage", // 4
            "Subcategory", // 5
            "Affiliation", //6
            "Project Year", // 7
            "Planned Start Date", // 8
            "Planned End Date", // 9
            "Actual Start Date", // 10
            "Actual End Date", // 11
            "Virus Families", // 12
            "Prototype Viruses", // 13
            "Test Viruses", // 14
            "Virus Targets", // 15
            "TRL", // 16
            "Responsible Person", // 17
            "Last Updated" // 18
        ]
        csv += columnNames.join(",");

        // Parent Elements
        this.visibleDeliverableParents.sort((x1: DeliverableParentExtended, x2: DeliverableParentExtended) => {
            let start1 = new Date(x1.DeliverableParent.PlannedStartDate)?.getTime();
            let start2 = new Date(x2.DeliverableParent.PlannedStartDate)?.getTime();
            if (start1 && start2) {
                return start1 - start2
            }
            else if (start1) return -1;
            else if (start2) return 1
            else {
                return 0;
            }
        });

        this.visibleDeliverableParents.forEach(row => {
            csv += "\n";
            csv += this.createCsvParentRow(row);
            row.DeliverableChildren.forEach(child => {
                csv += "\n";
                csv += this.createCsvChildRow(child);
            })
        });

        return csv;
    }

    createCsvParentRow(data: DeliverableParentExtended): string {
        let returnArr: string[] = [
            "Parent", // Type
            data.DeliverableParent.Description, // Description
            this.renderPickListItem(data.DeliverableParent.CategoryId, "pl-deliverable-category", false, true),// Category
            this.renderPickListItem(data.StatusId, "pl-deliverable-status", false, false), // Status
            this.renderDeliverableItem(data, 1), // Furthest Stage
            "", // Subcategory
            this.renderPickListItem(data.DeliverableParent.CenterId, "pl-center", false, false), // Center
            this.renderPickListItem(data.ProjectYearIds, "pl-project-year", true, false), // Project Year
            getDisplayDate(data.DeliverableParent.PlannedStartDate), // Planned Start
            getDisplayDate(data.DeliverableParent.PlannedEndDate), // Planned End
            getDisplayDate(data.DeliverableParent.ActualStartDate), // Actual Start
            getDisplayDate(data.DeliverableParent.ActualEndDate), // Actual End
            this.renderPickListItem(data.VirusFamilyIds, "pl-virus-family", true, false), // Virus Families
            this.renderPickListItem(data.DeliverableParent.PrototypeVirusIds, "pl-virus", true, false), // Prototype Virus
            this.renderPickListItem(data.DeliverableParent.TestVirusIds, "pl-virus", true, false), // Test Case Virus
            this.renderPickListItem(data.DeliverableParent.TargetIds, "pl-virus-target", true, false), // Virus Targets
            this.renderPickListItem(data.DeliverableParent.TrlTagId, "pl-trl-tag", false, true), // TRL Tag
            "", // Responsible Person
            getDisplayDate(data.LastUpdated) // Last Updated
        ];
        returnArr = returnArr.map(item => `"${item.replace("<br>", " - ").replace("<i>", "").replace("</i>", "")}"`);
        return returnArr.join(",");
    }

    createCsvChildRow(data: DeliverableChildExtended): string {
        let returnArr: string[] = [
            "Child", // Type
            data.DeliverableChild.Description, // Description
            this.renderPickListItem(data.DeliverableChild.CategoryId, "pl-deliverable-category", false, true),// Category
            this.renderPickListItem(data.StatusId, "pl-deliverable-status", false, false), // Status
            this.renderDeliverableChildItem(data, 1), // Furthest Stage
            this.renderDeliverableChildItem(data, 2), // Subcategory
            this.renderDeliverableChildItem(data, 3), // Center, Core, Project
            "", // Project Year
            "", // Planned Start
            "", // Planned End
            "", // Actual Start
            "", // Actual End
            "", // Virus Families
            "", // Prototype Virus
            "", // Test Case Virus
            "", // Virus Targets
            "", // TRL Tag
            this.renderPickListItem(data.DeliverableChild.ResponsiblePersonId, "pl-users", false, false), // Responsible Person
            getDisplayDate(data.LastUpdated)
        ];
        returnArr = returnArr.map(item => `"${item.replace("<br>", " - ").replace("<i>", "").replace("</i>", "") }"`);
        return returnArr.join(",");
    }

    getCurrentTableSort(): any[] {
        let currentOrder: any[] = this.deliverableTable.order(); // [[0, "asc"], [1, "desc"], ...]
        console.log(currentOrder);
        return currentOrder;
    }

    setCurrentTableSort(sortOrder: any[]): void {
        if (sortOrder.length > 0) {
            for (let i = 0; i < sortOrder.length; i++) {
                let colIndex = sortOrder[i][0];
                let colDir = sortOrder[i][1];
                this.deliverableTable.order([colIndex, colDir]);
            }
            this.deliverableTable.draw();
        }
    }

    scrollToParent(parentId: number = -1): void {
        let shouldAddExpandClass = false;

        // Used to indicate new item (will be the largest ID)
        if (parentId == -1) {
            let idArr: number[] = this.deliverableParents.map(parent => parent.DeliverableParent.Id);
            let maxVal = Math.max(...idArr);
            parentId = maxVal;
            shouldAddExpandClass = true;
        }

        let itemExists = false;
        let foundIndex = 0;

        // Find Parent Row
        this.deliverableTable.rows((index: number, data: DeliverableParentExtended, node) => {
            if (data?.DeliverableParent?.Id == parentId) {
                if (data?.DeliverableChildren?.length == 0) {
                    shouldAddExpandClass = true;
                }
                foundIndex = index;
                itemExists = true;
            }
            return false;
        });

        if (!itemExists) {
            // If target isn't found, do nothing
            return;
        }

        let foundRow = this.deliverableTable.row(foundIndex);

        // Expand Row
        let foundRowHtml = foundRow.node();
        if (shouldAddExpandClass) {
            $(foundRowHtml).addClass("expand-row"); // adds highlight even if there are no children (new parent elements)
        }
        else {
            $(foundRowHtml).find(".item-expand-option").trigger("click"); // expands child row
        }

        // Scroll to Parent Row
        let foundRowPosition = $(foundRowHtml).position().top;
        let tableBody = $(`#deliverable-table-container`).find(".dataTables_scrollBody");
        tableBody.animate({ scrollTop: foundRowPosition }, 400);
    }

    openEditModal(sectionId: string, isChild: string = "true"): void {
        if (isChild == "true") {
            this.editChildController.showLoading(false);
            this.editChildController.initialize(this.allPickLists, [this.currentTargetItem], sectionId);
            $(`#${this.editChildController.modalIdTarget}`).modal('show');
        }
        else {
            this.editParentController.showLoading(false);
            this.editParentController.initialize(this.allPickLists, [this.currentTargetItem], sectionId);
            $(`#${this.editParentController.modalIdTarget}`).modal('show');
        }
    }

    openAddModal(isChild: string = "true"): void {
        if (isChild == "true") {
            let parent: DeliverableParentExtended = this.currentTargetItem;
            let newChild: DeliverableChildExtended = DeliverableChildExtended.createFromParent(parent);

            this.addChildController.showLoading(true);
            this.addChildController.initialize(this.allPickLists, newChild, parent.DeliverableParent.Description);
            $(`#${this.addChildController.modalIdTarget}`).modal('show');
        }
        else {
            let newParent: DeliverableParentExtended = new DeliverableParentExtended();

            this.addParentController.showLoading(true);
            this.addParentController.initialize(this.allPickLists, newParent);
            $(`#${this.addParentController.modalIdTarget}`).modal('show');
        }
    }

    saveEditParentModal() {
        this.editParentController.saveEditModal();
    }

    saveEditChildModal() {
        this.editChildController.saveEditModal();
    }

    saveAddParentModal() {
        this.addParentController.saveAddModal();
    }

    saveAddChildModal() {
        this.addChildController.saveAddModal();
    }

    deleteDeliverable() {
        this.deleteDeliverableController.deleteDeliverableModal();
    }

    openCommentModal(): void {
        if (this.editChildCommentController) {
            this.editChildCommentController.initialize(this.currentTargetItem);
            $(`#${this.editChildCommentController.modalId}`).modal('show');
        }  
    }

    openAttachmentsModal(): void {
        if (this.editChildAttachmentsController) {
            this.editChildAttachmentsController.initialize(this.currentTargetItem);
            $(`#${this.editChildAttachmentsController.modalId}`).modal('show');
        }
    }

    openDeleteModal(isChild: string = "true"): void {
        if (this.deleteDeliverableController) {
            if (isChild == "true") {
                this.deleteDeliverableController.initializeChild(this.currentTargetItem);
            }
            else {
                this.deleteDeliverableController.initialize(this.currentTargetItem);
            }
            $(`#${this.deleteDeliverableController.modalId}`).modal("show");
        }
    }
}

